import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SbBlockComponent } from '../block/sb-block.component';
import { CommonModule } from '@angular/common';
import { Section } from '../../types/section.type';
import { PageSettings } from '../../types/page-settings.type';
import { PageLayoutService } from '../../services/page-resolver.service';
import { GlobalService } from '../../../common/services/global.service';
import { ListingPageSettings } from '../../../common/types/global-settings';
import { Subscription } from 'rxjs';
import { PopularCommunitiesService } from '../../../common/services/popular-communities.service';

@Component({
    selector: 'app-sb-section',
    standalone: true,
    imports: [SbBlockComponent, CommonModule],
    templateUrl: './sb-section.component.html',
    styleUrl: './sb-section.component.scss',
})
export class SbSectionComponent implements OnInit, OnDestroy {
    /**
     * Page Settings
     */
    @Input() pageSettings?: PageSettings;

    /**
     * Section
     */
    @Input() section!: Section;

    /**
     * Section index
     */
    @Input() index!: number;

    /**
     * Section classes
     */
    sectionClasses: string[] = [];

    /**
     * Default background color
     */
    backGroundColor: string = 'transparent';

    /**
     * Whether to render the section or not
     */
    shouldRender: boolean = false;

    /**
     * To subscribe the Route
     */
    private subscriptions = {
        listingPage: null as Subscription | null,
        popularCommunities: null as Subscription | null
    }

    // Dependency injections
    private globalService = inject(GlobalService);
    private popularCommunitiesService = inject(PopularCommunitiesService)

    ngOnInit(): void {
        this.setSectionClasses();
        this.subscriptions.listingPage =  this.globalService.listingPageSettings.subscribe(value => {
            this.checkWhetherToRender(value);
        })

        const component = this.section?.blocks?.[0]?.children?.[0];
        const componentName = component?.component;

        if (componentName == 'popular-communities') {
            this.subscriptions.popularCommunities =
                this.popularCommunitiesService.popularCommunitiesHasLength$.subscribe((value) => {
                    this.shouldRender = value;
                });
        }

        if (this.section.options.bg_color 
            && this.section.options.bg_color !== 'transparent' 
            && this.section.options.bg_color !== 'white'
            && this.section.options.bg_color.slice(0, 4).toLowerCase() !== '#fff' ) {
                this.backGroundColor = `var(--sb-bg-1)`;
            }
    }

    /**
     * Whether to render this section based on the listing page settings
     * @param listingPageSettings
     * @returns
     */
    private checkWhetherToRender(listingPageSettings: ListingPageSettings | null) {
        const component = this.section?.blocks?.[0]?.children?.[0];
        const componentName = component?.component;
        if (!componentName) {
            return;
        }
        switch (componentName) {
            case 'popular-searches': {                
                this.shouldRender = true;
            }
            break;
            case 'popular-communities': {                
                this.shouldRender = true;
            }
            break;
            case 'popular-cities': {
                this.shouldRender = listingPageSettings?.pages?.city === 'allow'
            }
            break;
            default: 
                this.shouldRender = true;
        }
    }

    /**
     * Set section classes
     */
    private setSectionClasses(): void {
        const options = this.section.options;

        // Add section classes for blog
        if (this.pageSettings?.pageLayoutType === 'blog-home-page') {
            this.sectionClasses.push('px-3 px-md-5 px-lg-9');
            this.sectionClasses.push('py-6');
            return;
        }

        if (this.pageSettings?.pageLayoutType === 'blog-page') {
            this.sectionClasses.push('px-0');
            return;
        }

        // Add section classes for property page
        if (this.pageSettings?.pageLayoutType === 'listing-page') {
            this.sectionClasses.push('px-0');
            this.sectionClasses.push('mb-5');
            return;
        }

        if (this.pageSettings?.pageLayoutType === 'custom-page') {
            this.sectionClasses.push('px-0');
            return;
        }

        if (options.container_padding_x === 0) {
            return;
        } else {
            this.sectionClasses.push('px-2 px-md-5 px-lg-7');

            // TODO: When button was added at the bottom of the property-listing and blog-list
            // padding of the section container was causing a issue with the button alignment
            // So added special condition to handle this
            // Need to find better and scalable solution for this
            if ((this.section?.blocks?.[0]?.children?.[0]?.component === 'property-listing'
                || this.section?.blocks?.[0]?.children?.[0]?.component === 'blog-list')
                && this.section?.blocks?.[0]?.children?.[0]?.inputs?.options?.['view_more'] === true
                && this.section?.blocks?.[0]?.children?.[0]?.inputs?.options?.['position'] === 'bottom') {
                    this.sectionClasses.push('py-4 py-lg-5');
            } else {
                this.sectionClasses.push('py-2 py-md-4 py-lg-6');
            }
        }
    }

    ngOnDestroy() {
        Object.values(this.subscriptions).forEach(sub => sub?.unsubscribe());
    }
}
